import { format, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';

const toDateInstance = (value: Date | string): Date => (value instanceof Date ? value : new Date(value));

export enum DateType {
  LocalDateTime,
  ZonedDateTime,
}

const extractZone = (date: string) => {
  const match = date.match(/\[(.+)\]$/);
  return match ? match[1] : undefined;
};

const stripZone = (date: string) => date.replace(/\[[^\]]+\]$/, '');

/**
 * 타임존이 있는 문자열을 UTC Date로 파싱
 */
const parseZonedDate = (date: string): Date => {
  const zone = extractZone(date);
  const base = stripZone(date);

  if (zone) {
    const parts = base.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (!parts) {
      return parseISO(base);
    }

    const year = parseInt(parts[1]);
    const month = parseInt(parts[2]) - 1;
    const day = parseInt(parts[3]);
    const hour = parseInt(parts[4]);
    const minute = parseInt(parts[5]);
    const second = parseInt(parts[6]);

    // UTC 시간으로 가정하고 Date 생성
    const dateInTz = new Date(Date.UTC(year, month, day, hour, minute, second));

    // 타임존 오프셋 계산
    const tzOffsetMs = getTimezoneOffsetMs(zone, dateInTz);

    // 오프셋을 빼서 UTC Date 생성
    return new Date(dateInTz.getTime() - tzOffsetMs);
  }

  return parseISO(base);
};

/**
 * 특정 타임존의 오프셋을 밀리초로 계산
 */
function getTimezoneOffsetMs(timezone: string, date: Date): number {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  return tzDate.getTime() - utcDate.getTime();
}

const DateUtils = {
  to_mmss: (second: number) => {
    return `${Math.floor(second / 60)
      .toString()
      .padStart(2, '0')}:${Math.floor(second % 60)
      .toString()
      .padStart(2, '0')}`;
  },
  toDatetime: (date: string, type: DateType = DateType.ZonedDateTime) => {
    const dateTime = DateUtils.parse(date, type);
    return format(dateTime, 'yyyy-MM-dd HH:mm:ss');
  },

  toDate: (date: string) => {
    const dateTime = DateUtils.parse(date, DateType.ZonedDateTime);
    return format(dateTime, 'yyyy-MM-dd');
  },

  toEpochSecond: (date: string) => {
    const parsed = DateUtils.parse(date, DateType.ZonedDateTime);
    return Math.trunc(parsed.getTime() / 1000);
  },

  toInstant: (str?: string) => DateUtils.parse(str ?? '', DateType.ZonedDateTime),

  nowDateTime: () => new Date().toISOString(),

  toNativeDate: (date: string) => {
    return DateUtils.parse(date, DateType.ZonedDateTime);
  },

  compare: (a: string, b: string, type: DateType = DateType.ZonedDateTime) => {
    const first = DateUtils.parse(a, type);
    const second = DateUtils.parse(b, type);
    const diff = first.getTime() - second.getTime();
    if (diff === 0) {
      return 0;
    }
    return diff > 0 ? 1 : -1;
  },

  parse: (date: string, type: DateType = DateType.ZonedDateTime): Date => {
    if (type === DateType.ZonedDateTime) {
      return parseZonedDate(date);
    }
    return parseISO(date);
  },

  getDaysInMonth: (year: number, month: number): number => {
    return new Date(year, month, 0).getDate();
  },

  formatSecondsToTime: (sec: number, formatStr: string = 'HH:mm:ss') => {
    const utcDate = new Date(sec * 1000);

    return formatInTimeZone(utcDate, 'UTC', formatStr);
  },

  toKoreanDate: (date: Date | string) => format(toDateInstance(date), 'yyyy년 MM월 dd일'),
  toShortKoreanDate: (date: Date | string) => format(toDateInstance(date), 'yy년 MM월 dd일'),
  toKoreanDateTime: (date: Date | string) => format(toDateInstance(date), 'yyyy년 MM월 dd일 HH:mm:ss'),
  toKoreanDateTimeMinute: (date: Date | string) => format(toDateInstance(date), 'yyyy년 MM월 dd일 HH:mm'),
  toKoreanDateTimeAmPm: (date: Date | string) =>
    format(toDateInstance(date), 'yyyy년 MM월 dd일 a hh:mm', { locale: ko }),
  toKoreanYearMonth: (date: Date | string) => format(toDateInstance(date), 'yyyy년 MM월'),
};

export default DateUtils;
